/**
 * Stage 2 of the hybrid track-changes engine — the word-level diff.
 *
 * Runs a Myers diff over the tokenized baseline and target arrays (Stage 1, `tokenize`) and emits
 * the canonical `Op[]` model (`./types`). We diff **token arrays** via the `diff` package's
 * `diffArrays`, so the library respects our exact tokenization instead of applying its own word
 * splitting. Pure function, no Word dependency, unit-tested.
 *
 * **Whitespace-insensitive matching.** The AI/manual target text is whitespace-collapsed (runs of
 * spaces become one), while the live baseline keeps its original spacing — including the double
 * spaces common in legal drafts. Comparing tokens exactly would then redline every spacing
 * difference (`"or  "` vs `"or "`), producing spurious delete+insert churn and mangled edits. So we
 * compare tokens with whitespace runs collapsed: `"or  "` equals `"or "` → a `keep`, no edit. Real
 * word changes still differ and are redlined normally.
 *
 * **Keeps carry the BASELINE text.** For an unchanged (whitespace-insensitive) run we emit the
 * baseline token, not the target token — so the document's real spacing is left untouched AND the
 * downstream char-offset cursor advances by the true baseline length (correct positioning).
 *
 * Guarantees the rest of the engine relies on:
 *  - **Baseline reconstruction.** `keep`+`delete` ops concatenate back to the baseline exactly
 *    (keeps and deletes both use baseline text). Target reconstruction is intentionally only
 *    whitespace-approximate — we do not churn spacing.
 *  - **Replacement contract for Stage 3.** A replacement surfaces as `delete`→`insert` adjacency,
 *    regardless of the library's added/removed ordering.
 */

import { diffArrays } from 'diff';
import { Op } from './types';

/** Collapse whitespace runs so spacing differences don't force a token to look changed. */
const collapseWs = (token: string): string => token.replace(/\s+/g, ' ');
const wsEqual = (a: string, b: string): boolean => collapseWs(a) === collapseWs(b);

/** A token-level diff group carrying both sides' tokens (keep has both; delete/insert one side). */
interface Group {
  kind: 'keep' | 'delete' | 'insert';
  bTokens: string[]; // baseline tokens (keep/delete)
  tTokens: string[]; // target tokens (keep/insert)
}

/**
 * Diff two token arrays (from `tokenize`) into the canonical op model, in baseline order.
 */
export function diffWords(baselineTokens: string[], targetTokens: string[]): Op[] {
  const changes = diffArrays(baselineTokens, targetTokens, {
    comparator: (a, b) => wsEqual(a, b),
  });

  // Build token-level groups with explicit baseline/target tokens.
  const groups: Group[] = [];
  let bi = 0;
  let ti = 0;
  for (const change of changes) {
    const n = change.count ?? change.value.length;
    if (change.added) {
      groups.push({ kind: 'insert', bTokens: [], tTokens: targetTokens.slice(ti, ti + n) });
      ti += n;
    } else if (change.removed) {
      groups.push({ kind: 'delete', bTokens: baselineTokens.slice(bi, bi + n), tTokens: [] });
      bi += n;
    } else {
      groups.push({
        kind: 'keep',
        bTokens: baselineTokens.slice(bi, bi + n),
        tTokens: targetTokens.slice(ti, ti + n),
      });
      bi += n;
      ti += n;
    }
  }

  slideInsertions(groups);

  // Stringify: keep/delete use BASELINE text (real doc + exact positioning); insert uses target text.
  const raw: Op[] = [];
  for (const g of groups) {
    if (g.kind === 'insert') {
      const value = g.tTokens.join('');
      if (value !== '') raw.push({ kind: 'insert', value });
    } else {
      const value = g.bTokens.join('');
      if (value !== '') raw.push({ kind: g.kind, value });
    }
  }

  return coalesce(normalizeReplacements(raw));
}

/**
 * Hunk sliding for readability. When an insertion sits between two keeps and its leading token
 * matches the following keep's leading token (common where text repeats), slide the insertion later:
 * keep the boundary token and rotate it to the end of the insertion. This turns
 *   `⟨ins: furnished or sold or are proposed to be⟩ furnished or sold by the Company`
 * into the natural
 *   `furnished or sold ⟨ins: or are proposed to be furnished or sold⟩ by the Company`.
 * Baseline reconstruction is unchanged (kept baseline tokens only move between adjacent keeps);
 * target content is preserved up to whitespace.
 */
function slideInsertions(groups: Group[]): void {
  for (let i = 0; i < groups.length; i++) {
    const ins = groups[i];
    if (ins.kind !== 'insert') continue;
    const prev = groups[i - 1];
    const next = groups[i + 1];
    if (!prev || prev.kind !== 'keep' || !next || next.kind !== 'keep') continue;

    while (
      ins.tTokens.length > 0 &&
      next.tTokens.length > 0 &&
      wsEqual(ins.tTokens[0], next.tTokens[0])
    ) {
      const kb = next.bTokens.shift() as string; // boundary baseline token
      const kt = next.tTokens.shift() as string; // boundary target token
      prev.bTokens.push(kb); // the boundary stays kept, now on the earlier keep
      prev.tTokens.push(kt);
      ins.tTokens.shift(); // drop the insertion's leading (now-kept) token
      ins.tTokens.push(kt); // rotate the boundary token to the end of the insertion
    }
  }
}

/**
 * Within every contiguous block of non-`keep` ops, emit all `delete`s before all `insert`s. This
 * makes a replacement always surface as `delete`→`insert` (Stage 3's precondition) no matter how
 * the diff library ordered added/removed runs.
 *
 * Safe for both reconstruction invariants: reordering happens only inside a run of non-`keep` ops,
 * and it never changes the relative order of `keep`+`delete` (inserts are absent from that
 * subsequence) nor of `keep`+`insert` (deletes are absent from that one).
 */
function normalizeReplacements(ops: Op[]): Op[] {
  const out: Op[] = [];
  let i = 0;
  while (i < ops.length) {
    if (ops[i].kind === 'keep') {
      out.push(ops[i]);
      i++;
      continue;
    }
    const block: Op[] = [];
    while (i < ops.length && ops[i].kind !== 'keep') {
      block.push(ops[i]);
      i++;
    }
    for (const op of block) if (op.kind === 'delete') out.push(op);
    for (const op of block) if (op.kind === 'insert') out.push(op);
  }
  return out;
}

/** Merge adjacent same-kind ops into one, concatenating values (keeps the model minimal). */
function coalesce(ops: Op[]): Op[] {
  const out: Op[] = [];
  for (const op of ops) {
    const prev = out[out.length - 1];
    if (prev && prev.kind === op.kind) prev.value += op.value;
    else out.push({ ...op });
  }
  return out;
}
